import {
  Box,
  Button,
  CurrencyIcon,
  EMPTY_ARRAY,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  isMultileg,
  SideEnum,
  Tooltip,
  useCallbackRef,
  useSecuritiesContext,
} from '@talos/kyoko';
import { capitalize, noop } from 'lodash-es';
import { type RefObject, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { UnifiedLiquidityTooltipIndicator } from '../MarketSelector/UnifiedLiquidityTooltipIndicator';
import { DefaultLegSummary } from './DefaultLegSummary';
import { MultilegMarketSelector } from './MultilegMarketSelector';
import { getMultilegLegSide } from './multilegSide';
import { Section } from './styles';
import type { MultiLegSummaryProps } from './types';
import { getUnifiedLiquidityLegSymbols } from './unifiedLiquidityLeg';
import { MULTILEG_UNIFIED_LIQUIDITY_LEG_INDEX as UL_LEG_INDEX, useMultilegLegButtons } from './useMultilegLegButtons';
import { useUnifiedLiquidityMarketDetails } from './useUnifiedLiquidityMarkets';

const SummaryPanelWrapper = styled(Box)``;
SummaryPanelWrapper.defaultProps = {
  borderRadius: 'borderRadiusDefault',
  mb: 'spacing8',
};

interface OpenMarketSelector {
  legIndex: number;
  buttonAnchorRef: RefObject<HTMLButtonElement>;
  panelAnchorRef: RefObject<HTMLDivElement>;
}

type MultilegLegDetails = NonNullable<NonNullable<MultiLegSummaryProps['security']['MultilegDetails']>['Legs']>;

export const DefaultMultilegSummary = ({
  security,
  // we default the order's side to be Buy for displaying multileg details if there is no order side specified
  // this can be useful in admin cases etc. In other cases such as orders / market data, this should always be specified
  side = SideEnum.Buy,
  readOnly,
  initiatingLegs,
  haircutRatios,
  legMarkets,
  legMarketSelectorItems,
  unifiedLiquidityToken,
  unifiedLiquidityMarkets,
  unifiedLiquidityMarketSelectorItems,
  supportsAllMarkets,
  touched,
  onEditSymbol,
  onUnifiedLiquidityMarketsChange,
  onLegMarketsChange,
  onInitiatingLegChange = noop,
  onHaircutRatioChange = noop,
  allocations,
  enableHaircutRatio,
  enableInPanelMarketSelectorLegSelection,
  isMarketSelectorNarrow,
  orderQuantity,
  orderCurrency,
  panelBottomSuffix,
}: MultiLegSummaryProps) => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const [expandedLeg, setExpandedLeg] = useState<number>(-1);
  const [openMarketSelector, setOpenMarketSelector] = useState<OpenMarketSelector | null>(null);
  const fallbackMarketSelectorAnchorRef = useRef<HTMLDivElement>(null);

  const defaultLegMarkets = useMemo(
    () => security.MultilegDetails?.Legs?.map(leg => leg.Markets?.map(m => m.MarketAccount) ?? []) ?? [[], []],
    [security]
  );
  // Selected market-account names for each regular leg. Missing leg values fall back to the instrument defaults.
  const resolvedLegMarkets = defaultLegMarkets.map((defaultLegMarketAccounts, index) => {
    return legMarkets?.at(index) ?? defaultLegMarketAccounts;
  });

  const defaultInitiatingLegs = useMemo(
    () => security.MultilegDetails?.Parameters?.LegParams?.map(legParam => legParam.Initiating) ?? [],
    [security]
  );
  const actualInitiatingLegs = initiatingLegs ?? defaultInitiatingLegs;

  const defaultHaircutRatios = useMemo(
    () => security.MultilegDetails?.Parameters?.LegParams?.map(legParam => legParam.HaircutRatio) ?? [],
    [security]
  );
  const actualHaircutRatios = haircutRatios ?? defaultHaircutRatios;

  const hasInitiatingError = !actualInitiatingLegs.some(t => t);
  const hasMarketError =
    !supportsAllMarkets &&
    (resolvedLegMarkets.some(m => m.length === 0) ||
      (unifiedLiquidityToken != null && unifiedLiquidityMarkets?.length === 0));
  const legDetails = security.MultilegDetails?.Legs ?? [];

  const availableULMarkets = useUnifiedLiquidityMarketDetails(unifiedLiquidityToken);
  const { representativeSymbol: ulRepresentativeSymbol, displaySymbols: ulDisplaySymbols } =
    getUnifiedLiquidityLegSymbols(unifiedLiquidityToken, securitiesBySymbol);
  const legMarketSelectorButtons = useMultilegLegButtons({
    enabled: enableInPanelMarketSelectorLegSelection,
    resolvedLegMarkets,
    legDetails,
    unifiedLiquidityMarkets,
    unifiedLiquidityDisplaySymbols: ulDisplaySymbols,
    includeUnifiedLiquidity: !!unifiedLiquidityToken,
    showAllMarketsToggle: supportsAllMarkets,
  });

  const handleMarketSelectorChange = useCallbackRef((value: string[]) => {
    if (!openMarketSelector) {
      // Shouldn't happen. Type-checking. We need the openMarketSelector.legIndex to exist before proceeding.
      return;
    }

    if (openMarketSelector.legIndex === UL_LEG_INDEX) {
      onUnifiedLiquidityMarketsChange?.(value);
      return;
    }

    onLegMarketsChange?.({ legIndex: openMarketSelector.legIndex, value });
  });

  const handleOpenMarketSelector = useCallbackRef((params: OpenMarketSelector) => {
    // Called from the buttons in the LegSummary components. When one of these buttons is clicked, we also make sure to set the
    // different refs, to re-orient the market selector to the button / section we clicked from
    setOpenMarketSelector(params);
  });

  const handleMarketSelectorPanelLegClick = useCallbackRef((legIndex: number) => {
    // Called from within the market selector panel. We only update the legIndex, and not the positioning anchor refs.
    // This makes it so the staying-open market selector panel stays positioned where it initially was opened, despite switching legs.
    setOpenMarketSelector(current => (current ? { ...current, legIndex } : current));
  });

  if (!isMultileg(security)) {
    return null;
  }
  if (!security.MultilegDetails?.Legs?.length) {
    return null;
  }

  const errors = capitalize(
    [
      hasInitiatingError ? 'At least 1 leg must Initiate' : undefined,
      hasMarketError ? 'Markets are required for all legs' : undefined,
    ]
      .compact()
      .join(' and ')
  );

  const openMarketSelectorProps = getOpenMarketSelectorProps({
    resolvedLegMarkets,
    legDetails,
    legMarketSelectorItems,
    openLegIndex: openMarketSelector?.legIndex,
    orderSide: side,
    securitiesBySymbol,
    unifiedLiquidityMarketSelectorItems,
    unifiedLiquidityMarkets,
    ulSymbol: ulRepresentativeSymbol,
  });
  return (
    <SummaryPanelWrapper data-testid="multileg-summary-panel">
      <Section isExpanded={false} nonExpandedRows={1}>
        <HStack flex={1} justifyContent="space-between" gap="spacing4" my="spacing4">
          <HStack gap="spacing4" color="colorTextImportant" overflow="hidden">
            <CurrencyIcon currency={security.BaseCurrency} size={12} stacked={!!unifiedLiquidityToken} />
            <Box lineHeight={1} whiteSpace="nowrap" fontSize="fontSizeSmall" overflow="hidden" textOverflow="ellipsis">
              {security?.DisplaySymbol}
              {unifiedLiquidityToken && (
                <UnifiedLiquidityTooltipIndicator
                  baseSymbol={unifiedLiquidityToken.Symbol}
                  tokens={unifiedLiquidityToken.Tokens}
                  dataTestId="multileg-summary-unified-liquidity-tooltip"
                  statusTestIdPrefix="multileg-summary-unified-liquidity-tooltip-status"
                  showStatus={false}
                />
              )}
            </Box>
          </HStack>
          {hasInitiatingError || hasMarketError ? (
            <Tooltip tooltip={errors}>
              <Icon
                icon={IconName.Exclamation}
                data-testid="multileg-summary-initiating-error"
                color="colorTextNegative"
              />
            </Tooltip>
          ) : null}
          {onEditSymbol && !readOnly && (
            <Box m="-spacing2">
              <Tooltip tooltip="Edit multileg symbol">
                <Button
                  data-testid="edit-multileg-symbol"
                  size={FormControlSizes.Tiny}
                  startIcon={IconName.Pencil}
                  onClick={() => onEditSymbol(security.Symbol)}
                >
                  Edit
                </Button>
              </Tooltip>
            </Box>
          )}
        </HStack>
      </Section>

      {legDetails.map((leg, index) => {
        return (
          <DefaultLegSummary
            /** Whenever the symbol we are showing changes, we remount the component. There is code which needs to re-init when the symbol changes.
             * One example is the code to compute the available markets.
             */
            key={`${security.Symbol}-${leg.Symbol}-${index}`}
            legIndex={index}
            symbol={leg.Symbol}
            isExpanded={expandedLeg === index}
            onToggle={() => setExpandedLeg(index === expandedLeg ? -1 : index)}
            isInitiating={actualInitiatingLegs.at(index)}
            haircutRatio={actualHaircutRatios.at(index)}
            side={getMultilegLegSide(side, leg.Direction)}
            prettyLegName={leg.Symbol}
            legMarketNames={resolvedLegMarkets.at(index) ?? EMPTY_ARRAY}
            marketDetails={leg.Markets ?? undefined}
            readOnly={readOnly || onLegMarketsChange == null}
            showLegMarketChanges={false}
            touched={touched}
            supportsAllMarkets={supportsAllMarkets}
            onOpenMarketSelector={handleOpenMarketSelector}
            onInitiatingLegChange={onInitiatingLegChange}
            onHaircutRatioChange={onHaircutRatioChange}
            enableHaircutRatio={enableHaircutRatio}
          />
        );
      })}
      {unifiedLiquidityToken && ulRepresentativeSymbol ? (
        <DefaultLegSummary
          /** Whenever the symbol we are showing changes, we remount the component. There is code which needs to re-init when the symbol changes.
           * One example is the code to compute the available markets.
           */
          key={`${security.Symbol}-${unifiedLiquidityToken.Symbol}`}
          legIndex={undefined}
          symbol={ulRepresentativeSymbol}
          isExpanded={expandedLeg === 2}
          onToggle={() => setExpandedLeg(2 === expandedLeg ? -1 : 2)}
          isInitiating={actualInitiatingLegs.at(2)}
          haircutRatio={actualHaircutRatios.at(2)}
          side={side} // the order's side is used for the UL leg
          prettyLegName={ulDisplaySymbols}
          legMarketNames={unifiedLiquidityMarkets ?? EMPTY_ARRAY}
          marketDetails={availableULMarkets}
          readOnly={readOnly}
          touched={touched}
          showLegMarketChanges={false}
          supportsAllMarkets={supportsAllMarkets}
          onOpenMarketSelector={handleOpenMarketSelector}
          onInitiatingLegChange={onInitiatingLegChange}
          onHaircutRatioChange={onHaircutRatioChange}
          enableHaircutRatio={enableHaircutRatio}
        />
      ) : null}
      <MultilegMarketSelector
        legIndex={openMarketSelectorProps.legIndex}
        isOpen={openMarketSelector != null}
        close={() => setOpenMarketSelector(null)}
        security={openMarketSelectorProps.security}
        value={openMarketSelectorProps.value}
        items={openMarketSelectorProps.items}
        showAllMarketsToggle={!openMarketSelectorProps.isUnifiedLiquidity && supportsAllMarkets}
        onChange={handleMarketSelectorChange}
        marketSelectorAnchorRef={openMarketSelector?.panelAnchorRef ?? fallbackMarketSelectorAnchorRef}
        dropdownAnchorRef={openMarketSelector?.buttonAnchorRef ?? fallbackMarketSelectorAnchorRef}
        side={openMarketSelectorProps.side}
        subAccountAllocations={allocations}
        legButtons={legMarketSelectorButtons}
        onLegClick={enableInPanelMarketSelectorLegSelection ? handleMarketSelectorPanelLegClick : undefined}
        isMarketSelectorNarrow={isMarketSelectorNarrow}
        orderQuantity={orderQuantity}
        orderCurrency={orderCurrency}
        panelBottomSuffix={panelBottomSuffix}
        showSpinner={false}
      />
    </SummaryPanelWrapper>
  );
};

/**
 * Resolves the data needed by the single top-level market selector for whichever leg is currently active.
 *
 * The selector can be rendered before a leg has been opened, so the first branch returns harmless empty defaults.
 * Once open, `openLegIndex` is either a real leg index or `UL_LEG_INDEX`.
 */
function getOpenMarketSelectorProps({
  resolvedLegMarkets,
  legDetails,
  legMarketSelectorItems,
  openLegIndex,
  orderSide,
  securitiesBySymbol,
  unifiedLiquidityMarketSelectorItems,
  unifiedLiquidityMarkets,
  ulSymbol,
}: {
  resolvedLegMarkets: string[][];
  legDetails: MultilegLegDetails;
  legMarketSelectorItems: MultiLegSummaryProps['legMarketSelectorItems'];
  openLegIndex: number | undefined;
  orderSide: SideEnum;
  securitiesBySymbol: ReturnType<typeof useSecuritiesContext>['securitiesBySymbol'];
  unifiedLiquidityMarketSelectorItems: MultiLegSummaryProps['unifiedLiquidityMarketSelectorItems'];
  unifiedLiquidityMarkets: MultiLegSummaryProps['unifiedLiquidityMarkets'];
  ulSymbol: string | undefined;
}) {
  if (openLegIndex == null) {
    // Nothing opened yet, we can just return an empty result set
    return {
      legIndex: undefined,
      isUnifiedLiquidity: false,
      security: undefined,
      value: EMPTY_ARRAY,
      items: undefined,
      side: orderSide,
    };
  }

  if (openLegIndex === UL_LEG_INDEX) {
    // UL market selection is backed by one representative token symbol for security/enrichment context,
    // while its available market list is still derived from every token in the UL response.
    return {
      legIndex: undefined,
      isUnifiedLiquidity: true,
      security: ulSymbol ? securitiesBySymbol.get(ulSymbol) : undefined,
      value: unifiedLiquidityMarkets ?? EMPTY_ARRAY,
      items: unifiedLiquidityMarketSelectorItems,
      side: orderSide,
    };
  }

  const leg = legDetails.at(openLegIndex);

  // Regular leg selection is straightforward: the open index points at the leg security,
  // its selected market accounts, and optionally prebuilt selector items for that leg.
  return {
    legIndex: openLegIndex,
    isUnifiedLiquidity: false,
    security: leg?.Symbol ? securitiesBySymbol.get(leg.Symbol) : undefined,
    value: resolvedLegMarkets.at(openLegIndex) ?? EMPTY_ARRAY,
    items: legMarketSelectorItems?.at(openLegIndex),
    side: leg == null ? orderSide : getMultilegLegSide(orderSide, leg.Direction),
  };
}
